import { differenceInMinutes, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { APIError } from "@/adapters/api";
import { currentWorkDate, dateOnlyToDate, dateToDateOnly } from "@/lib/format";
import type { TimeEntry } from "../types";

export const EMPTY_VALUE = "—";
export const NO_LOCATION_MESSAGE = "Ponto registrado sem localização.";

export function formatClock(iso: string | null | undefined): string {
	if (!iso) return EMPTY_VALUE;
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
	return format(date, "HH:mm");
}

export function formatMinutes(total: number | null | undefined): string {
	if (total == null || total < 0) return EMPTY_VALUE;
	const hours = Math.floor(total / 60);
	const minutes = total % 60;
	return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function entryMinutes(entry: TimeEntry, now: Date): number {
	if (entry.totalMinutes != null) return entry.totalMinutes;
	const start = new Date(entry.clockedInAt);
	if (Number.isNaN(start.getTime())) return 0;
	return Math.max(0, differenceInMinutes(now, start));
}

export function dayLabel(date: Date): string {
	return format(date, "EEE, dd/MM", { locale: ptBR });
}

export function workDateLabel(workDate: string): string {
	const date = dateOnlyToDate(workDate);
	return date ? dayLabel(date) : workDate;
}

export function isToday(date: Date): boolean {
	return dateToDateOnly(date) === currentWorkDate();
}

export function timeToISO(workDate: string, time: string): string | undefined {
	const day = dateOnlyToDate(workDate);
	if (!day || !/^\d{2}:\d{2}$/.test(time)) return undefined;
	const [hours, minutes] = time.split(":").map(Number);
	if (hours == null || minutes == null) return undefined;
	if (hours > 23 || minutes > 59) return undefined;
	const parsed = new Date(day);
	parsed.setHours(hours, minutes, 0, 0);
	return parsed.toISOString();
}

export function isoToTime(iso: string | null | undefined): string {
	if (!iso) return "";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "";
	return format(date, "HH:mm");
}

export function formatSignedMinutes(total: number): string {
	if (total <= 0) return EMPTY_VALUE;
	return `+${formatMinutes(total)}`;
}

export function formatPercent(value: number): string {
	return `${Number.isInteger(value) ? value : value.toFixed(2)}%`;
}

const REASON_MESSAGES: Record<string, string> = {
	"time-entry-overlap": "Este horário se sobrepõe a outro registro.",
	"time-entry-not-open": "Não há entrada aberta para registrar a saída.",
	"time-clock-disabled": "O bate ponto está desativado para este membro.",
	"status/conflicts_with_entries":
		"Remova os registros do dia antes de marcar falta/atestado/folga.",
	"edit_note/required": "Alterações em dias anteriores exigem justificativa.",
	"date/future": "Não é possível editar um dia futuro.",
	"file/unsupported_type": "Envie um arquivo PDF, JPG ou PNG.",
	"file/empty": "O arquivo está vazio.",
};

function violationMessage(err: APIError): string | undefined {
	for (const [field, description] of Object.entries(err.violations)) {
		const message =
			REASON_MESSAGES[`${field}/${description}`] ??
			(description.startsWith("max_size:")
				? "O arquivo excede o tamanho máximo permitido."
				: undefined);
		if (message) return message;
	}
	return undefined;
}

export function timeEntryErrorMessage(err: unknown, fallback: string): string {
	if (err instanceof APIError) {
		const message =
			REASON_MESSAGES[err.reason] ??
			REASON_MESSAGES[err.code] ??
			violationMessage(err);
		if (message) return message;
		return err.message || fallback;
	}
	return fallback;
}
